import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminMetrics } from '@/types/admin-metrics.types';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-metrics`;

type Status = 'idle' | 'loading' | 'success' | 'error';

interface UseAdminMetricsResult {
  status: Status;
  data: AdminMetrics | null;
  error: string | null;
  lastFetchedAt: Date | null;
  refetch: () => void;
}

/**
 * Fetches AdminMetrics from the password-gated edge function. Auto-refreshes
 * every 60 seconds while the document is visible. The password is passed in
 * by the AdminShell ref so we never persist it in storage.
 */
export function useAdminMetrics(password: string): UseAdminMetricsResult {
  const [status, setStatus] = useState<Status>('idle');
  const [data, setData] = useState<AdminMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const inFlight = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!password || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) {
        const body = await res
          .json()
          .catch(() => ({ error: `HTTP ${res.status}` }));
        const base = body.error ?? `HTTP ${res.status}`;
        const view =
          typeof body.view === 'string' && body.view ? ` [${body.view}]` : '';
        const msg =
          typeof body.message === 'string' && body.message !== body.error
            ? `: ${body.message}`
            : '';
        throw new Error(`${base}${view}${msg}`);
      }
      const json: AdminMetrics = await res.json();
      setData(json);
      setLastFetchedAt(new Date());
      setStatus('success');
      setError(null);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      inFlight.current = false;
    }
  }, [password]);

  useEffect(() => {
    fetchOnce();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchOnce();
    }, 60_000);
    return () => clearInterval(interval);
  }, [fetchOnce]);

  return { status, data, error, lastFetchedAt, refetch: fetchOnce };
}
